/**
 * The safety guards, checked against a run's counts — for a Try, which the
 * server runs with the guards off, so a person still sees what would trip.
 *
 * The same sums as `omnium_core/integrations/service.py`: held rows count as
 * not placed, and shares are of the rows that were considered.
 */

import type { Settings } from "@/lib/integrations/settings";
import type { Tone } from "@/lib/integrations/words";

export type GuardCheck = { key: string; label: string; passed: boolean; detail: string };

function percent(share: number): string {
  return `${Math.round(share * 100)}%`;
}

export function guardChecks(
  counts: Record<string, number>,
  rowsCollected: number,
  safety: Settings["safety"],
  usualRows: number | null,
): GuardCheck[] {
  const out: GuardCheck[] = [];
  if (usualRows && safety.minShare > 0) {
    const passed = rowsCollected >= safety.minShare * usualRows;
    out.push({
      key: "few_rows",
      label: passed ? "Row count normal" : "Far fewer rows than usual",
      passed,
      detail: `Scout sent ${rowsCollected.toLocaleString()} rows; it usually sends about ${Math.round(usualRows).toLocaleString()}.`,
    });
  } else {
    out.push({
      key: "few_rows",
      label: "Row count",
      passed: true,
      detail: safety.minShare > 0 ? "No written runs yet to compare with." : "This guard is off.",
    });
  }
  const considered =
    (counts.accepted ?? 0) +
    (counts.unchanged ?? 0) +
    (counts.kept_by_hand ?? 0) +
    (counts.refused ?? 0) +
    (counts.not_placed ?? 0) +
    (counts.held ?? 0);
  const notPlaced = (counts.not_placed ?? 0) + (counts.held ?? 0);
  const refused = counts.refused ?? 0;
  const share = (part: number) => (considered ? part / considered : 0);
  out.push({
    key: "not_placed",
    label: `Not placed ${percent(share(notPlaced))} (limit ${percent(safety.maxNotPlaced)})`,
    passed: share(notPlaced) <= safety.maxNotPlaced,
    detail: `${notPlaced.toLocaleString()} of ${considered.toLocaleString()} rows were not placed or held.`,
  });
  out.push({
    key: "refused",
    label: `Refused ${percent(share(refused))} (limit ${percent(safety.maxRefused)})`,
    passed: share(refused) <= safety.maxRefused,
    detail: `${refused.toLocaleString()} of ${considered.toLocaleString()} rows were refused by a rule.`,
  });
  return out;
}

/**
 * A guard drawn as a gauge: its value on a track, and a mark where it trips.
 *
 * `fill` and `mark` are shares of the track (0 to 1). For the two "too many"
 * guards the track runs to twice the limit, so the mark sits in the middle and
 * a value near it is easy to see; a value past the track fills it.
 */
export type GuardGauge = {
  key: "few_rows" | "not_placed" | "refused";
  label: string;
  off: boolean;
  tripped: boolean;
  /** Good well inside the limit, warning close to it, danger past it, neutral when off or nothing to measure. */
  tone: Tone;
  /** The value in words: "0%", "No runs to compare yet". */
  valueText: string;
  /** The limit in words: "pauses above 10%". */
  limitText: string;
  fill: number;
  mark: number;
  ifTrips: string;
};

/** A share in words, never rounding a real value down to nothing. */
export function shareWords(share: number): string {
  if (share > 0 && share < 0.005) return "under 1%";
  return `${Math.round(share * 100)}%`;
}

const clamp = (value: number) => Math.max(0, Math.min(1, value));

export function guardGauges(
  counts: Record<string, number>,
  rowsCollected: number,
  safety: Settings["safety"],
  usual: number | null,
): GuardGauge[] {
  const considered =
    (counts.accepted ?? 0) +
    (counts.unchanged ?? 0) +
    (counts.kept_by_hand ?? 0) +
    (counts.refused ?? 0) +
    (counts.not_placed ?? 0) +
    (counts.held ?? 0);

  const fewOff = safety.minShare <= 0;
  const ofUsual = usual && usual > 0 ? rowsCollected / usual : null;
  const fewScale = Math.max(1, Math.min(2, ofUsual ?? 1));
  const fewTripped = !fewOff && ofUsual !== null && ofUsual < safety.minShare;
  const few: GuardGauge = {
    key: "few_rows",
    label: "Rows compared with usual",
    off: fewOff,
    tripped: fewTripped,
    tone: fewOff || ofUsual === null ? "neutral" : fewTripped ? "danger" : ofUsual < safety.minShare * 1.2 ? "warning" : "good",
    valueText: ofUsual === null ? "No runs to compare yet" : `${shareWords(ofUsual)} of usual`,
    limitText: fewOff ? "Off" : `pauses below ${shareWords(safety.minShare)}`,
    fill: ofUsual === null ? 0 : clamp(ofUsual / fewScale),
    mark: fewOff ? 0 : clamp(safety.minShare / fewScale),
    ifTrips: "Nothing is written. It pauses and asks “Write anyway?”.",
  };

  const tooMany = (key: "not_placed" | "refused", label: string, part: number, limit: number, ifTrips: string): GuardGauge => {
    const off = limit >= 1;
    const share = considered > 0 ? part / considered : 0;
    const scale = off ? 1 : Math.min(1, Math.max(limit * 2, 0.02));
    const tripped = !off && share > limit;
    return {
      key,
      label,
      off,
      tripped,
      tone: off || considered === 0 ? "neutral" : tripped ? "danger" : share > limit / 2 ? "warning" : "good",
      valueText: considered > 0 ? shareWords(share) : "No rows yet",
      limitText: off ? "Off" : `pauses above ${shareWords(limit)}`,
      fill: clamp(share / scale),
      mark: off ? 0 : clamp(limit / scale),
      ifTrips,
    };
  };

  return [
    few,
    tooMany(
      "not_placed",
      "Not placed or held",
      (counts.not_placed ?? 0) + (counts.held ?? 0),
      safety.maxNotPlaced,
      "Nothing from the run is written. It pauses with the list.",
    ),
    tooMany(
      "refused",
      "Refused by rules",
      counts.refused ?? 0,
      safety.maxRefused,
      "The whole run is taken back. It pauses with the refused rows.",
    ),
  ];
}

/** The usual row count: the average of the last five written runs, as the server measures it. */
export function usualRows(runs: { status: string; rowsCollected: number }[]): number | null {
  const written = runs.filter((run) => run.status === "written" || run.status === "nothing_new").slice(0, 5);
  if (written.length === 0) return null;
  return written.reduce((sum, run) => sum + run.rowsCollected, 0) / written.length;
}
